import './polyfills';

import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { isProdMode } from './config';
import { apiProviders } from './api';
import { serviceProviders } from './services';
import {
  StorageV2Service,
  UnitsService,
  UnitsApi,
  UserV2Service,
  UserV2Api,
  UNITS_BASE_URL,
  AZURE_ENDPOINT,
  MultipleTranslatePoHttpLoader,
  ModalModule,
  PagesV2Module,
  ButtonV2Module,
  InputV2Module,
  IconV2Module,
  GridV2Module,
  SelectV2Module,
  TabModule,
  OutputV2Module,
  WorkflowModule,
  FormsModuleV2,
  SearchInputV2Module,
  CheckboxV2Module,
  DateV2Module,
} from '@schenck/rotec-components';
import { API_BASE_URL } from './api/tokens';
import { environment } from './environment/environment';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  ProtectedResourceScopes,
  MsalInterceptor,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  HttpClient,
} from '@angular/common/http';
import { ServerErrorInterceptor } from './services/interceptors/server-error.interceptor';
import { ComponentsModule } from './components/components.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ModalsModule } from './components/modals/modals.module';
import { MatMenuModule } from '@angular/material/menu';
import { AppComponent } from './app.component';
import { bootstrapApplication } from '@angular/platform-browser';
import deLocale from '@angular/common/locales/de';
import enLocale from '@angular/common/locales/en';
import esLocale from '@angular/common/locales/es';
import frLocale from '@angular/common/locales/fr';
import itLocale from '@angular/common/locales/it';
import nlLocale from '@angular/common/locales/nl';
import svLocale from '@angular/common/locales/sv';
import plLocale from '@angular/common/locales/pl';
import trLocale from '@angular/common/locales/tr';
import zhLocale from '@angular/common/locales/zh';
import huLocale from '@angular/common/locales/hu';
import csLocale from '@angular/common/locales/cs';
import koLocale from '@angular/common/locales/ko';
import jaLocale from '@angular/common/locales/ja';
import ptLocale from '@angular/common/locales/pt';
import { registerLocaleData } from '@angular/common';
import {
  provideRouter,
  Routes,
  withDisabledInitialNavigation,
  withEnabledBlockingInitialNavigation,
  withHashLocation,
  withDebugTracing,
} from '@angular/router';
import { routes } from './utils/constants/routes';
import { AuthGuard } from './guards/auth.guard';
import { LoggedInGuard } from './guards/logged-in.guard';

registerLocaleData(deLocale, 'de');
registerLocaleData(enLocale, 'en');
registerLocaleData(esLocale, 'es');
registerLocaleData(frLocale, 'fr');
registerLocaleData(itLocale, 'it');
registerLocaleData(nlLocale, 'nl');
registerLocaleData(svLocale, 'sv');
registerLocaleData(plLocale, 'pl');
registerLocaleData(trLocale, 'tr');
registerLocaleData(zhLocale, 'zh');
registerLocaleData(huLocale, 'hu');
registerLocaleData(csLocale, 'cs');
registerLocaleData(koLocale, 'ko');
registerLocaleData(jaLocale, 'ja');
registerLocaleData(ptLocale, 'pt');

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

if (isProdMode) {
  enableProdMode();
}

function HttpPoLoaderFactory(http: HttpClient) {
  return new MultipleTranslatePoHttpLoader(http, [
    { prefix: 'assets/rotec-locales/', suffix: '.po' },
    { prefix: 'assets/locales/', suffix: '.po' },
  ]);
}

function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: environment.authority,
      redirectUri: environment.redirectUrl,
      postLogoutRedirectUri: environment.redirectUrl,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [environment.scope],
    },
  };
}

function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string | ProtectedResourceScopes>>([
    [`${environment.clientId}`, [`${environment.scope}`]],
    [`${environment.apiBaseUrl}`, [`${environment.scope}`]],
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export const appRoutes: Routes = [
  {
    path: routes.tenants.url,
    loadComponent: () =>
      import('./pages/tenants/tenants.component').then((m) => m.TenantsComponent),
    canActivate: [AuthGuard],
  },
  {
    path: routes.users.url,
    loadComponent: () => import('./pages/users/users.component').then((m) => m.UsersComponent),
    canActivate: [AuthGuard],
  },
  {
    path: routes.machines.url,
    loadComponent: () =>
      import('./pages/machines/machines.component').then((m) => m.MachinesComponent),
    canActivate: [AuthGuard],
  },
  {
    path: routes.login.url,
    loadComponent: () => import('./pages/login/login.component').then((m) => m.LoginComponent),
    canActivate: [LoggedInGuard],
  },
  { path: '**', redirectTo: routes.tenants.url },
];

const isIframe = window !== window.parent && !window.opener;

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(
      appRoutes,
      isIframe ? withDisabledInitialNavigation() : withEnabledBlockingInitialNavigation(),
      withHashLocation(),
      withDebugTracing()
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpPoLoaderFactory,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(
      ComponentsModule,
      ReactiveFormsModule,
      // Rotec Components
      ModalModule.forRoot(),
      PagesV2Module,
      ButtonV2Module,
      InputV2Module,
      IconV2Module,
      GridV2Module,
      SelectV2Module,
      TabModule,
      OutputV2Module,
      WorkflowModule,
      FormsModuleV2,
      ModalsModule,
      SearchInputV2Module,
      MatMenuModule,
      CheckboxV2Module,
      DateV2Module
    ),
    ...apiProviders,
    ...serviceProviders,
    StorageV2Service,
    UnitsService,
    UnitsApi,
    UserV2Service,
    UserV2Api,
    { provide: API_BASE_URL, useValue: environment.apiBaseUrl },
    { provide: UNITS_BASE_URL, useValue: 'unitsBaseUrl', multi: true },
    { provide: AZURE_ENDPOINT, useValue: 'azureUrl', multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
}).catch((err) => console.log(err));
